/**
 * CSP-compliant dark mode utilities.
 *
 * Vuetify 2.6.14's `theme.dark` setter calls `applyTheme()` which — even with
 * `theme: { disable: true }` — runs `clearCss() → set css('') →
 * checkOrCreateStyleElement() → styleEl.innerHTML = ''`, injecting/modifying a
 * `<style>` element and violating strict CSP `style-src` without 'unsafe-inline'.
 *
 * These helpers centralise the workaround so every call site stays consistent.
 */

export const DARK_MODE_STORAGE_KEY = 'cassie-dark-mode'

/**
 * Set `$vuetify.theme.dark` without triggering style injection.
 *
 * Temporarily replaces `applyTheme` with a no-op so the setter only updates
 * the internal `isDark` flag (consumed by the Themeable mixin for class
 * toggling). The static CSS in `vuetify-theme-static.css` provides the actual
 * theme--dark / theme--light colour rules.
 *
 * @param {object} vuetify - The `$vuetify` framework instance
 * @param {boolean} isDark
 */
export function setVuetifyDark (vuetify, isDark) {
	if (!vuetify) return
	const theme = vuetify.theme
	const origApply = theme.applyTheme
	theme.applyTheme = () => {}
	try {
		theme.dark = isDark
	} finally {
		theme.applyTheme = origApply
	}
}

/**
 * Apply dark mode to the document and Vuetify instance.
 *
 * - Toggles the `dark` class on `<html>` (drives CSS custom properties)
 * - Sets `$vuetify.theme.dark` via the CSP-safe helper (drives Themeable mixin)
 *
 * @param {object} vuetify - The `$vuetify` framework instance
 * @param {boolean} isDark
 */
export function applyDarkMode (vuetify, isDark) {
	const root = document.documentElement
	if (isDark) {
		root.classList.add('dark')
	} else {
		root.classList.remove('dark')
	}
	setVuetifyDark(vuetify, isDark)
}

/**
 * Read the persisted dark-mode preference from localStorage.
 *
 * @returns {boolean|null} `true` / `false` if a preference is stored, `null` otherwise
 */
export function loadDarkModePreference () {
	try {
		const saved = localStorage.getItem(DARK_MODE_STORAGE_KEY)
		if (saved === 'true') return true
		if (saved === 'false') return false
	} catch {
		// localStorage unavailable
	}
	return null
}

/**
 * Persist the dark-mode preference to localStorage.
 *
 * @param {boolean} isDark
 */
export function saveDarkModePreference (isDark) {
	try {
		localStorage.setItem(DARK_MODE_STORAGE_KEY, String(isDark))
	} catch {
		// localStorage unavailable
	}
}
